import {
  cardDividerBorderClass,
  cardHoverSurfaceClass,
  cardMutedTextClass,
  cardSurfaceClass,
  cardTitleTextClass,
} from "@/components/common/surfaceClasses";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { type ReactNode, useId, useState } from "react";

type Props = {
  title: string;
  subtitle?: string;
  defaultOpen: boolean;
  children: ReactNode;
  /** Shown in header row (e.g. counts, close). Clicks do not toggle the section. */
  headerRight?: ReactNode;
  /** Controlled open; when set, `onOpenChange` is used for toggles. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** TOUR-5 — optional `data-firefly-target` on the outer section. */
  fireflyTargetId?: string;
};

export function SalesConsoleCollapsibleSection({ title, subtitle, defaultOpen, children, headerRight, open: controlledOpen, onOpenChange, fireflyTargetId }: Props) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen! : uncontrolledOpen;
  const panelId = useId();

  function setOpen(next: boolean) {
    if (isControlled) onOpenChange?.(next);
    else setUncontrolledOpen(next);
  }

  function toggle() {
    setOpen(!open);
  }

  return (
    <section
      className={`${cardSurfaceClass} overflow-hidden`}
      {...(fireflyTargetId ? { [FIREFLY_TARGET_ATTR]: fireflyTargetId } : {})}
    >
      <div className={`flex flex-wrap items-stretch gap-0 border-b ${cardDividerBorderClass}`}>
        <button
          type="button"
          className={`flex-1 min-w-0 text-left px-4 py-3 ${cardHoverSurfaceClass}`}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={toggle}
        >
          <h2 className={`text-base font-semibold ${cardTitleTextClass}`}>{title}</h2>
          {subtitle ? <p className={`text-xs mt-0.5 ${cardMutedTextClass}`}>{subtitle}</p> : null}
        </button>
        <div className="flex items-center gap-1 shrink-0 pr-2 py-2">
          {headerRight}
          <button
            type="button"
            className={`rounded-lg px-2 py-1.5 text-xs ${cardMutedTextClass} hover:bg-gray-100 dark:hover:bg-zinc-800`}
            aria-label={open ? "Collapse section" : "Expand section"}
            onClick={toggle}
          >
            {open ? "▲" : "▼"}
          </button>
        </div>
      </div>
      {open ? (
        <div id={panelId} className="px-4 pb-4 pt-3">
          {children}
        </div>
      ) : null}
    </section>
  );
}
